import { useEffect, useState } from "react";
import {
  Box, Paper, Button, Alert, TextField, InputAdornment,
  Dialog, DialogContent, DialogActions, CircularProgress,
  FormControlLabel, Switch, Grid2 as Grid, Stack, Typography,
} from "@mui/material";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import { PageHeader } from "@/components/PageHeader";
import { RowActions } from "@/components/RowActions";
import { StatusPill } from "@/components/StatusPill";
import { AccountPicker } from "@/components/AccountPicker";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { serviceGroupsApi, tenantsApi, type ServiceGroupDto, type AccountDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

/**
 * Service group master. Groups bucket service items (Consultation, Radiology, Ward…) and, for
 * tenants that map the chart of accounts one level up, carry the revenue account head for the
 * whole bucket. The page adapts to the tenant's ServiceAccountMappingMode: under group mapping
 * an unmapped group is a real problem, so it is called out rather than shown as an empty cell.
 */
export function ServiceGroupsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceGroupDto | null>(null);

  const canManage = useAuth(s => s.hasPermission("sales.servicegroup.manage"));

  const tenant = useQuery({
    queryKey: ["tenant", "current"],
    queryFn: tenantsApi.current,
    staleTime: 5 * 60_000,
  });
  const groupMapped = tenant.data?.serviceAccountMappingMode === "ServiceGroup";

  const query = useQuery({
    queryKey: ["service-groups", search, pagination.page, pagination.pageSize],
    queryFn: () => serviceGroupsApi.list({
      search: search || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const deleteMutation = useMutation({
    meta: { successMessage: "Service group deleted" },
    mutationFn: (id: string) => serviceGroupsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["service-groups"] }),
  });

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (row: ServiceGroupDto) => { setEditing(row); setFormOpen(true); };
  const handleDelete = (row: ServiceGroupDto) => {
    if (confirm(`Delete group "${row.code} — ${row.name}"?`)) deleteMutation.mutate(row.id);
  };

  const unmappedUnderGroupMode = groupMapped
    ? (query.data?.items ?? []).filter(g => g.isActive && !g.glAccountId && g.serviceItemCount > 0)
    : [];

  const columns: GridColDef<ServiceGroupDto>[] = [
    { field: "code", headerName: "Code", width: 130 },
    {
      // W23 — a flat list that reads as the tree it is. The parent under the name is what tells
      // "Radiology" and "Radiology — CT" apart at a glance, which is the confusion the hierarchy
      // was added to end.
      field: "name", headerName: "Group", flex: 1, minWidth: 220,
      renderCell: (p) => (
        <Stack spacing={0} sx={{ py: 0.5 }}>
          <span>{p.row.name}</span>
          {p.row.parentName
            ? (
              <Typography variant="caption" color="text.secondary">
                under {p.row.parentName}
              </Typography>
            )
            : p.row.childCount > 0 && (
              <Typography variant="caption" color="text.secondary">
                department · {p.row.childCount} sub-group{p.row.childCount === 1 ? "" : "s"}
              </Typography>
            )}
        </Stack>
      ),
    },
    {
      field: "glAccountCode", headerName: "Account head", flex: 1, minWidth: 200,
      renderCell: (p) => {
        if (p.row.glAccountCode) return `${p.row.glAccountCode} — ${p.row.glAccountName ?? ""}`;
        return groupMapped && p.row.serviceItemCount > 0
          ? <StatusPill label="Not mapped" tone="warning" />
          : <Box component="span" sx={{ color: "text.disabled" }}>—</Box>;
      },
    },
    {
      field: "serviceItemCount", headerName: "Services", width: 110, align: "right", headerAlign: "right",
    },
    { field: "displayOrder", headerName: "Order", width: 90, align: "right", headerAlign: "right",
      valueGetter: (_v, row) => row.displayOrder ?? "—" },
    {
      field: "isActive", headerName: "Status", width: 110, sortable: false,
      renderCell: (p) => <StatusPill label={p.row.isActive ? "Active" : "Inactive"} />,
    },
    {
      field: "actions", headerName: "", width: 130, sortable: false, filterable: false,
      renderCell: (p) => (
        <RowActions actions={[
          { label: "Edit", icon: <EditIcon fontSize="small" />, hidden: !canManage, onClick: () => openEdit(p.row) },
          {
            label: "Services in this group", icon: <ListAltIcon fontSize="small" />,
            onClick: () => navigate(`/catalog/services?groupId=${p.row.id}`),
          },
          {
            label: "Delete", icon: <DeleteIcon fontSize="small" />, color: "error", hidden: !canManage,
            disabledReason: p.row.serviceItemCount > 0
              ? "Reassign its services first, or mark the group inactive"
              // W23 — deleting a parent would leave its children pointing at nothing.
              : p.row.childCount > 0
              ? "Move the groups under it out first, or mark this one inactive"
              : undefined,
            onClick: () => handleDelete(p.row),
          },
        ]} />
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Catalog"]}
        title="Service Groups"
        subtitle={groupMapped
          ? "This tenant maps revenue account heads on the group, so every group holding services needs one."
          : "Optional buckets over service items. This tenant maps account heads on the service item itself."}
        actions={canManage ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New group</Button>
        ) : undefined}
      />

      {unmappedUnderGroupMode.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {unmappedUnderGroupMode.length} group(s) hold service items but carry no account head:{" "}
          <strong>{unmappedUnderGroupMode.map(g => g.code).join(", ")}</strong>. Those services fall back
          to their own head until you map one here.
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <TextField
          placeholder="Search by code or name"
          fullWidth size="small" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 2 }}
        />
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {deleteMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowDoubleClick={(p) => canManage && openEdit(p.row)}
          />
        </Box>
      </Paper>

      <GroupDialog open={formOpen} onClose={() => setFormOpen(false)} row={editing} groupMapped={groupMapped} />
    </Box>
  );
}

function GroupDialog({
  open, onClose, row, groupMapped,
}: { open: boolean; onClose: () => void; row: ServiceGroupDto | null; groupMapped: boolean }) {
  const qc = useQueryClient();
  const isEdit = !!row;
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [displayOrder, setDisplayOrder] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [glAccountId, setGlAccountId] = useState("");
  const [account, setAccount] = useState<AccountDto | null>(null);
  /** W23 — the group this one sits under. "" keeps it a department. */
  const [parentId, setParentId] = useState("");

  useEffect(() => {
    if (!open) return;
    setCode(row?.code ?? "");
    setName(row?.name ?? "");
    setDescription(row?.description ?? "");
    setDisplayOrder(row?.displayOrder != null ? String(row.displayOrder) : "");
    setIsActive(row?.isActive ?? true);
    setGlAccountId(row?.glAccountId ?? "");
    setParentId(row?.parentId ?? "");
    setAccount(null); // AccountPicker resolves from initialId on the next render
  }, [open, row]);

  // Candidate parents. The group itself is excluded — the server refuses it, and offering a choice
  // that always fails is worse than not offering it. Its descendants would also be refused; those
  // are left in the list because filtering them client-side means fetching the whole tree, and the
  // server's message names the group that clashes.
  const parents = useQuery({
    queryKey: ["service-groups", "parent-options"],
    queryFn: () => serviceGroupsApi.list({ isActive: true, pageSize: 200 }),
    staleTime: 5 * 60_000, enabled: open,
  });

  const mutation = useMutation({
    meta: { successMessage: isEdit ? "Service group updated" : "Service group created" },
    mutationFn: async () => {
      const payload = {
        code: code.trim(), name: name.trim(),
        description: description.trim() || null,
        glAccountId: glAccountId || null,
        displayOrder: displayOrder === "" ? null : Number(displayOrder),
        parentId: parentId || null,
        isActive,
      };
      if (isEdit && row) { await serviceGroupsApi.update(row.id, payload); return row.id; }
      const r = await serviceGroupsApi.create(payload);
      return r.id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["service-groups"] });
      qc.invalidateQueries({ queryKey: ["services"] });
      onClose();
    },
  });

  const err = !code.trim() ? "Code required" : !name.trim() ? "Name required" : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={(e) => { e.preventDefault(); if (!err) mutation.mutate(); }}>
        <ClosableDialogTitle onClose={onClose}>{isEdit ? "Edit service group" : "New service group"}</ClosableDialogTitle>
        <DialogContent dividers>
          {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Code *" size="small" fullWidth autoFocus
                value={code} onChange={(e) => setCode(e.target.value)} inputProps={{ maxLength: 50 }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField label="Name *" size="small" fullWidth
                value={name} onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 200 }} />
            </Grid>
            <Grid size={12}>
              <TextField label="Description" size="small" fullWidth multiline minRows={2}
                value={description} onChange={(e) => setDescription(e.target.value)}
                inputProps={{ maxLength: 500 }} />
            </Grid>
            {/* W23 — where this group sits. A department has no parent; a sub-group names one and
                inherits its revenue head when it states none of its own. */}
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField select SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
                label="Sits under" size="small" fullWidth
                value={parentId} onChange={(e) => setParentId(e.target.value)}
                helperText={parentId
                  ? "Rolls up into that group, and inherits its account head if none is set here"
                  : "Blank = a department, at the top of the tree"}>
                <option value="">— Top level (department) —</option>
                {(parents.data?.items ?? [])
                  .filter(g => g.id !== row?.id)
                  .map(g => (
                    <option key={g.id} value={g.id}>
                      {g.code} — {g.name}{g.parentName ? ` (under ${g.parentName})` : ""}
                    </option>
                  ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }} />
            <Grid size={{ xs: 12, sm: 8 }}>
              <AccountPicker
                label={groupMapped ? "Revenue account head *" : "Revenue account head"}
                value={account}
                initialId={glAccountId || undefined}
                onChange={(v) => { setAccount(v); setGlAccountId(v?.id ?? ""); }}
                helperText={groupMapped
                  ? "Every service in this group posts here"
                  : "Only used if a service in this group has no head of its own"}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Display order" size="small" fullWidth type="number"
                value={displayOrder} onChange={(e) => setDisplayOrder(e.target.value)} />
            </Grid>
            <Grid size={12}>
              <FormControlLabel label="Active"
                control={<Switch checked={isActive} onChange={(_, v) => setIsActive(v)} />} />
            </Grid>
          </Grid>
          {groupMapped && !glAccountId && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This tenant maps account heads on the group. Without one, services in this group fall
              back to their own head — and the API blocks removing a head from a group that already
              holds services.
            </Alert>
          )}
          {err && <Alert severity="info" sx={{ mt: 2 }}>{err}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!!err || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
